import { useInventoryTypesQuery } from '@/api/composables/inventory-types'
import { InventoryType } from '@ironsight/types'
import { computed, Ref } from 'vue'
import { useStore } from 'vuex-composition-helpers'

export const useInventoryTypesWithResourceTypeActivity = (
  resourceTypeActivityId: Ref<number | null>,
) => {
  const useInventoryTypesComposable = useInventoryTypes()

  const store = useStore()
  const resourceTypeActivity = computed(() =>
    store.getters['activities/getById'](resourceTypeActivityId.value),
  )

  const filteredInventoryTypesByResourceTypeActivityId = computed(() =>
    filterInventoryTypesByResourceTypeActivity(
      useInventoryTypesComposable.allInventoryTypes.value ?? [],
      resourceTypeActivity.value ?? null,
    ),
  )

  return {
    resourceTypeActivity,
    filteredInventoryTypesByResourceTypeActivityId,
    ...useInventoryTypesComposable,
  }
}

export const useInventoryTypes = () => {
  const {
    data: allInventoryTypes,
    isFetched,
    isFetching,
    isInitialLoading,
  } = useInventoryTypesQuery()

  const getInventoryTypeById = (id: number) => {
    return allInventoryTypes.value?.find((x) => x.id === id)
  }
  const getInventorySubTypeById = (id: number) => {
    if (!allInventoryTypes.value) return undefined

    for (const inventoryType of allInventoryTypes.value) {
      const subType = inventoryType.inventorySubTypes.find((subType) => subType.id === id)
      if (subType) return subType
    }
    return undefined
  }

  const getInventoryTypeOfSubTypeId = (id: number) => {
    if (!allInventoryTypes.value) return undefined

    for (const inventoryType of allInventoryTypes.value) {
      const subType = inventoryType.inventorySubTypes.find((subType) => subType.id === id)
      if (subType) return inventoryType
    }
    return undefined
  }

  return {
    allInventoryTypes,
    isFetched,
    isFetching,
    isInitialLoading,
    getInventoryTypeById,
    getInventorySubTypeById,
    getInventoryTypeOfSubTypeId,
  }
}

const filterInventoryTypesByResourceTypeActivity = (
  allInventoryTypes: InventoryType[],
  resourceTypeActivity: any | null, // ResourceTypeActivity | null
) => {
  if (!resourceTypeActivity) {
    return allInventoryTypes
  }
  return filterInventoryTypesByAssociations(
    allInventoryTypes,
    resourceTypeActivity.inventoryAssociations,
  )
}

export const filterInventoryTypesByAssociations = <
  T extends {
    id: number | null
    inventorySubTypes?: any[]
  },
>(
  allInventoryTypes: T[],
  inventoryAssociations: any[], // ResourceTypeActivityInventoryAssociation[]
): T[] => {
  if (inventoryAssociations.length === 0) {
    return allInventoryTypes
  }
  const result: T[] = []

  // Build a map of inventory type ID to its allowed sub-types
  const inventoryConfig = new Map<number, Set<number | null>>()

  for (const association of inventoryAssociations) {
    const typeId = association.inventoryTypeId

    if (!inventoryConfig.has(typeId)) {
      inventoryConfig.set(typeId, new Set())
    }

    inventoryConfig.get(typeId)!.add(association.inventorySubTypeId)
  }

  // Process each configured inventory type
  for (const [inventoryTypeId, allowedSubTypes] of inventoryConfig) {
    const inventoryType = allInventoryTypes.find((inv) => inv.id === inventoryTypeId)

    if (!inventoryType) continue

    // If null is in allowedSubTypes, include all sub-types
    if (allowedSubTypes.has(null)) {
      result.push(inventoryType)
      continue
    }

    // Otherwise, filter to only allowed sub-types
    const subTypes = inventoryType.inventorySubTypes || []
    const filteredInventory = {
      ...inventoryType,
      inventorySubTypes: subTypes.filter((subType: any) => allowedSubTypes.has(subType.id)),
    }

    result.push(filteredInventory as T)
  }

  return result
}
